import React from "react";
import {
  getAllUsersDetails,
  getAllUsersMeta,
  softDeleteUser,
  hardDeleteUser,
  restoreUser,
  getRenterVerificationSummary,
} from "../utils";
import { useState, useEffect } from "react";
import DataTable from "../UsersDataTable";

export default function Users() {
  const [allUsers, setAllUsers] = useState([]);
  const [usersMeta, setUsersMeta] = useState({});
  const [showDeleted, setShowDeleted] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const [allUsersData, allUsersMeta] = await Promise.all([
          getAllUsersDetails(),
          getAllUsersMeta(),
        ]);
        const usersMetaMap = (allUsersMeta || []).reduce((acc, meta) => {
          const userId = meta.uid || meta.userId;
          if (userId) {
            acc[userId] = meta;
          }
          return acc;
        }, {});
        setAllUsers(allUsersData);
        setUsersMeta(usersMetaMap);
      } catch (error) {
        console.error("Error fetching renters:", error);
      }
    };

    fetchUsers();
  }, []);

  const handleSoftDelete = async (userId, e) => {
    e.stopPropagation();
    if (
      !window.confirm(
        "Are you sure you want to delete this renter? This action can be undone by an admin."
      )
    )
      return;
    try {
      await softDeleteUser(userId);
      setAllUsers((prev) =>
        prev.map((u) => (u.userId === userId ? { ...u, isDeleted: true } : u))
      );
    } catch (error) {
      console.error("Error deleting renter:", error);
    }
  };

  const handleHardDelete = async (userId, e) => {
    e.stopPropagation();
    if (
      !window.confirm(
        "WARNING: This will PERMANENTLY delete this renter and all their data. This action CANNOT be undone. Are you sure?"
      )
    )
      return;
    try {
      await hardDeleteUser(userId);
      setAllUsers((prev) => prev.filter((u) => u.userId !== userId));
    } catch (error) {
      console.error("Error permanently deleting renter:", error);
    }
  };

  const handleRestore = async (userId, e) => {
    e.stopPropagation();
    if (!window.confirm("Are you sure you want to restore this renter?"))
      return;
    try {
      await restoreUser(userId);
      setAllUsers((prev) =>
        prev.map((u) => (u.userId === userId ? { ...u, isDeleted: false } : u))
      );
    } catch (error) {
      console.error("Error restoring renter:", error);
    }
  };

  const columns = [
    {
      Header: "BBR ID",
      accessor: "bbrId",
    },
    {
      Header: "Full Name",
      accessor: "name",
      Cell: ({ value, row }) => (
        <div className="all-renters-name-cell">
          <span>{value}</span>
          {row.original.verificationStatus === "verified" && (
            <span className="all-renters-status-pill all-renters-status-pill--verified">
              {row.original.verified}
            </span>
          )}
          {row.original.verificationStatus === "pending" && (
            <span className="all-renters-status-pill all-renters-status-pill--pending">
              Pending
            </span>
          )}
        </div>
      ),
    },
    {
      Header: "Status",
      accessor: "verified",
      Cell: ({ row }) => {
        const status = row.original.verificationStatus;
        const detail = row.original.verificationDetail;

        return (
          <div className="all-renters-status-cell">
            <span
              className={`all-renters-status-text all-renters-status-text--${status}`}
            >
              {row.original.verified}
            </span>
            {detail ? (
              <span className="all-renters-status-detail">{detail}</span>
            ) : null}
          </div>
        );
      },
    },
    {
      Header: "Phone",
      accessor: "phone",
    },
    {
      Header: "Email",
      accessor: "email",
    },
    {
      Header: "Address Line 1",
      accessor: "address1",
    },
    {
      Header: "City",
      accessor: "city",
    },
    {
      Header: "State/Province",
      accessor: "state",
    },
    {
      Header: "Zip Code",
      accessor: "zipCode",
    },
    {
      Header: "Country",
      accessor: "country",
    },
    {
      Header: "Action",
      accessor: "action",
      disableSortBy: true,
    },
  ];

  const linkUsersAndUsersMeta = () => {
    return allUsers
      .map((user) => {
        if (
          user.role === "Renter" &&
          (showDeleted ? user.isDeleted : !user.isDeleted)
        ) {
          const userId = user.userId || user.id;
          const summary = getRenterVerificationSummary(
            user,
            usersMeta[userId]
          );
          const fullName = [user.firstName, user.lastName]
            .filter(Boolean)
            .join(" ");

          return {
            ...user,
            name: fullName,
            verified: summary.label,
            verificationStatus: summary.status,
            verificationDetail: summary.detail,
            address1: user.address1 || "-",
            city: user.city || "-",
            state: user.state || "-",
            zipCode: user.zipCode || "-",
            country: user.country || "-",
          };
        }

        return null;
      })
      .filter(Boolean);
  };

  const data = linkUsersAndUsersMeta();

  return (
    <>
      <div className="row">
        <div className="col">
          <div className="page-description">
            <h1>{showDeleted ? "Deleted Renters" : "All Renters"}</h1>
          </div>
        </div>
        <div className="col-auto">
          <button
            className={`btn ${
              showDeleted ? "btn-primary" : "btn-outline-secondary"
            } btn-sm`}
            onClick={() => setShowDeleted(!showDeleted)}
          >
            {showDeleted ? "Show Active Renters" : "Show Deleted Renters"}
          </button>
        </div>
      </div>

      <div className="row">
        <div className="col">
          <div className="card calendar-container">
            <div className="card-body">
              <DataTable
                columns={columns}
                data={data}
                onDelete={showDeleted ? handleHardDelete : handleSoftDelete}
                onRestore={showDeleted ? handleRestore : null}
                deleteMode={showDeleted ? "permanent" : "soft"}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
